/**
 * GutschriftCheckFeedPage — the farmer-facing "Öffnen" view (#1289 C6).
 *
 * Same shell as the canvas page (LensePanel sidebar + FilterBar status
 * select/dashboard tiles + ``?ym=`` as the single month source of
 * truth + auto-advance + PageLoadOverlay under its OWN telemetry
 * descriptor), with the centre swapped from MatchGraph to the
 * paginated DeliveryGroupFeed. No mark/unmark mutations, no
 * MatchGraph import — the folded rows expose no resolution actions, so
 * the persisted ``manually_resolved_at`` attribute is sufficient for the
 * resolved-exclusion.
 *
 * #1397 — the per-document LS/GS badges (#1400) are clickable: this page
 * owns ``selectedDocId`` and mounts ``DocumentPreviewPanel`` as a flex
 * sibling of the feed (same shell slot the editor's canvas uses), reused
 * from the editor as-is. The panel auto-hides (returns ``null``) when
 * ``selectedDocId`` is null, so the feed reclaims full width on close.
 *
 * Pipeline order (pinned by tests): resolved-exclusion → status
 * filter → two-tier sort. NO page slice here — the pagination
 * contract's single owner: this page owns ``currentPage`` (reset to 0
 * on month/status change) and hands the FULL sorted list to
 * DeliveryGroupFeed, which owns the slice.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { LensePanel, PageLoadOverlay, useMobileSidePanel } from '@hofsync/design-system';
import { useMatchGroups, type MatchGroupStatus } from '../../../hooks/useMatchGroups';
import { usePageLoadTelemetry } from '../../../hooks/usePageLoadTelemetry';
import FilterBar from './FilterBar';
import DeliveryGroupFeed from './DeliveryGroupFeed';
import DocumentPreviewPanel from './DocumentPreviewPanel';
import { applySuppression, isOperatorResolved } from './resolvedExclusion';
import { sortDeliveryGroups } from './sortDeliveryGroups';
import { isSeasonMode, monthsToLenseItems } from './monthLensItems';
// Reuse the canvas page's shell classes (two-column LensePanel layout,
// empty/error states) so the two views can't drift; feed-specific
// styling lives in GutschriftCheckFeedPage.css.
import './GutschriftCheckPage.css';
import './GutschriftCheckFeedPage.css';

// #1804 — mirrors the editor page's ``currentYM`` (GutschriftCheckPage.tsx):
// both views share the same default-latest month model now that the feed's
// season roll-up is gone (option (a)).
function currentYM(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function GutschriftCheckFeedPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  // #1804 — month and status are decoupled (option (a)): ``?ym`` is ALWAYS a
  // concrete ``YYYY-MM``. With no explicit month the page falls back to the
  // implicit current month and the auto-advance effect below lands on the
  // latest month WITH data — the same model the editor uses. A legacy
  // ``?ym=gesamt`` deep link (the removed season roll-up) is treated as "no
  // explicit month" via ``isSeasonMode``, so it never reaches ``useMatchGroups``
  // and auto-advances to the latest month too. ``hasExplicitMonth`` gates the
  // bounce strictly to the initial-load path (a deliberate deep link stays put).
  const rawYm = searchParams.get('ym');
  const hasExplicitMonth = rawYm != null && !isSeasonMode(rawYm);
  const tenantMonth = hasExplicitMonth ? rawYm : currentYM();

  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  // #1397 — the document the lifted preview panel shows; null = no
  // preview (panel returns null, feed reclaims full width). Single source
  // of truth shared by the badge onClick and the panel's onClose, exactly
  // as the editor page wires its canvas selection.
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  const { groups, availableMonths, gsCountsByMonth, gsCountsByYear, isLoading, error } =
    useMatchGroups(tenantMonth);

  // #916 overlay primitive — own descriptor so the feed and the canvas
  // stay separable in the ELK buckets.
  const pageLoadOverlay = usePageLoadTelemetry({
    page: 'gutschrift-check-feed',
    view: `gutschrift-check-feed:ym=${tenantMonth}${statusFilter ? `:status=${statusFilter}` : ''}`,
    isLoading,
  });

  const handleMonthChange = useCallback(
    // #1804 — ``replace`` is used by the load-time auto-advance so the
    // pre-normalisation URL (bare no-``ym`` or the legacy ``?ym=gesamt``
    // sentinel) is NOT left on the history stack — otherwise a browser Back
    // would land on it and the effect would immediately bounce forward again
    // (a dead Back button). Explicit month picks (lens clicks) keep the
    // default PUSH so Back returns to the previously viewed month.
    (ym: string, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('ym', ym);
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  // #1804 — the dashboard's leading "Gesamt" tile is now a pure STATUS
  // control: it clears the status filter and NEVER touches ``?ym`` (month and
  // status are decoupled). FilterBar falls back to ``onStatusChange('')`` when
  // no ``onSelectGesamt`` is passed, so the feed simply omits the prop.

  // Reset pagination whenever the visible set's inputs change — month
  // (any path: lens click, auto-advance, deep link, back/forward) or
  // status filter. Mirrors /documents' reset-on-filter-change effect.
  useEffect(() => {
    setCurrentPage(0);
  }, [tenantMonth, statusFilter]);

  // #1397 — clear the lifted preview when the MONTH changes (any path:
  // lens click, auto-advance, deep link, back/forward). Without this the
  // panel would keep showing/downloading a previous-month document after
  // the feed has moved on. Keyed on ``tenantMonth`` only — a status-filter
  // change keeps the same month, so the previewed document may still be
  // in view and shouldn't be force-closed. The editor clears this in its
  // ``handleMonthChange`` handler; an effect covers the extra non-handler
  // month-change paths the feed exposes too.
  useEffect(() => {
    setSelectedDocId(null);
  }, [tenantMonth]);

  // #1287 lens-panel selection wiring — replicated from the canvas
  // page (see its comments for the yearOverride/mobile-close
  // rationale).
  const [yearOverride, setYearOverride] = useState<string | null>(null);
  const expandedYear = yearOverride ?? tenantMonth.slice(0, 4);

  useEffect(() => {
    setYearOverride(null);
  }, [tenantMonth]);

  const mobilePanel = useMobileSidePanel();
  const suppressNextMobileCloseRef = useRef(false);

  const handleLensSelect = useCallback(
    (itemId: string, subItemId: string | null) => {
      if (subItemId) {
        handleMonthChange(subItemId);
        setYearOverride(null);
        return;
      }
      // #1511 — the sidebar is the year→month tree only now; the Gesamt
      // lens entry is gone (moved to the dashboard panel), so every
      // null-sub-item select here is a year-header toggle.
      if (mobilePanel?.isOpen) {
        suppressNextMobileCloseRef.current = true;
      }
      setYearOverride(itemId === expandedYear ? '' : itemId);
    },
    [handleMonthChange, expandedYear, mobilePanel],
  );

  const handleMobileClose = useCallback(() => {
    if (suppressNextMobileCloseRef.current) {
      suppressNextMobileCloseRef.current = false;
      return;
    }
    mobilePanel?.close();
  }, [mobilePanel]);

  // #1511 — the lens sidebar is the year→month tree ONLY. The "Gesamt"
  // roll-up never renders here; it lives on the dashboard panel's leading
  // "Gesamt" tile (FilterBar), which is a pure status control (#1804).
  const lensItems = useMemo(() => {
    return monthsToLenseItems(availableMonths, gsCountsByMonth, gsCountsByYear);
  }, [availableMonths, gsCountsByMonth, gsCountsByYear]);

  // #1804 — default-latest auto-advance, mirroring the editor
  // (GutschriftCheckPage.tsx:457-475). If the page was opened without an
  // explicit ``?ym=`` (or with the legacy ``?ym=gesamt`` sentinel) AND the
  // implicit current month is empty, advance to the most recent month with
  // data so the farmer lands somewhere productive. ``hasExplicitMonth`` gates
  // the bounce strictly to the initial-load path — a deliberate deep link to
  // an empty month stays put (otherwise the month switcher reads as broken).
  // ``availableMonths`` is the hook's unfiltered entity set.
  useEffect(() => {
    if (hasExplicitMonth) return;
    if (isLoading || groups.length > 0) return;
    if (availableMonths.length === 0) return;
    const latestYM = availableMonths[availableMonths.length - 1];
    if (latestYM && latestYM !== tenantMonth) {
      // ``replace`` — this is a default-normalisation redirect, not a
      // user navigation, so it must not leave a Back-trap history entry.
      handleMonthChange(latestYM, { replace: true });
    }
  }, [hasExplicitMonth, isLoading, groups, tenantMonth, handleMonthChange, availableMonths]);

  // #1395 — suppression is applied FIRST (persisted attribute only — the
  // feed exposes no kebab, so no optimistic overrides): an erroneous doc
  // suppressed in the editor disappears from this farmer-facing view AND
  // its dashboard counts. The suppression-stripped set feeds BOTH the
  // FilterBar (totals) and the feed pipeline below so they agree.
  const suppressedStripped = useMemo(() => applySuppression(groups), [groups]);

  // Pipeline: suppression → resolved-exclusion (no overrides — persisted
  // attribute only) → status filter → two-tier sort. FULL list; the feed
  // slices.
  const visibleGroups = useMemo(() => {
    const unresolved = suppressedStripped.filter((g) => !isOperatorResolved(g));
    const filtered = statusFilter
      ? unresolved.filter((g) => g.status === (statusFilter as MatchGroupStatus))
      : unresolved;
    return sortDeliveryGroups(filtered);
  }, [suppressedStripped, statusFilter]);

  return (
    <div className="gutschrift-check-page">
      <LensePanel
        title={t('appPanels.gutschriftCheck', 'GS-Check')}
        items={lensItems}
        // #1804 — a concrete month is ALWAYS selected: the year (``expandedYear``
        // = yearOverride ?? the month's year) is the selected item and the month
        // is the active sub-item, so the selected month's year is always expanded
        // (the season-default fold bug is gone). Expanding another year header
        // (``yearOverride`` set to a real year) drives ``selectedItem`` so
        // LensePanel expands its months; collapse (``yearOverride === ''``)
        // falls back to the selected month's own year.
        selectedItem={expandedYear}
        selectedSubItem={tenantMonth}
        onSelect={handleLensSelect}
        isMobileOpen={mobilePanel?.isOpen ?? false}
        onMobileClose={handleMobileClose}
      />
      <PageLoadOverlay
        visible={pageLoadOverlay.showOverlay}
        elapsedSeconds={pageLoadOverlay.elapsedSeconds}
      />
      {/* Dashboard gets the suppression-stripped month set (#1395) — it
          applies its own resolved-exclusion via the shared
          resolvedExclusion helpers, which is what keeps tile counts and
          feed rows in agreement. #1394 — the editor-exit link rides in
          the tiles/header panel (was a standalone feed toolbar);
          Decision 5's ``?ym=`` carries across the feed→editor hop so the
          editor opens on the same month. */}
      <FilterBar
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        groups={suppressedStripped}
        // #2113 — the farmer-facing overview is the ONLY host that opts
        // into the plain-language summary sentence; the /editor canvas
        // omits the prop (a data-administration surface, where it is
        // noise). Gated on a clean, settled load: FilterBar cannot see
        // ``isLoading`` / ``error`` itself, and a confidently-wrong
        // sentence over half-loaded data is worse than none — the error
        // hint below owns that state. The BAR still renders in both
        // states (deliberate); only the sentence is withheld.
        showSummary={!isLoading && error == null}
        // #1804 — no ``onSelectGesamt``: the Gesamt tile is a pure status
        // control now, so FilterBar's fallback (``onStatusChange('')``) clears
        // the filter without ever touching ``?ym``.
        navLink={{
          // #1804 — ``tenantMonth`` is always a concrete ``YYYY-MM`` now, so the
          // Editor link carries the same month across the hop (#1394 Decision 5).
          to: `/apps/gutschrift-check/editor?ym=${tenantMonth}`,
          label: 'Editor',
        }}
      />

      <div className="gutschrift-check-page__workspace">
        <div className="gutschrift-check-feed-page__main">
          {/* Codex round-1 P2: error wins over the empty state — a
              failed load otherwise reads as "this month has no data".
              With partial data the hint renders above the feed. */}
          {!isLoading && error != null && (
            <div className="gutschrift-check-page__error" role="status">
              Hinweis: Einige Daten konnten nicht geladen werden.
            </div>
          )}
          {!isLoading && error == null && visibleGroups.length === 0 && (
            <div className="gutschrift-check-page__empty">
              {/* #1804 — ``tenantMonth`` is always a concrete month now, so the
                  empty state always names the "für <Monat Jahr>" it applies to. */}
              {(() => {
                const [y, m] = tenantMonth.split('-').map(Number);
                const label = new Date(y, m - 1, 1).toLocaleDateString('de-DE', {
                  month: 'long',
                  year: 'numeric',
                });
                return `Keine Lieferscheine oder Gutschriften für ${label}.`;
              })()}
            </div>
          )}
          {!isLoading && visibleGroups.length > 0 && (
            <DeliveryGroupFeed
              groups={visibleGroups}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              // Pass the suppression-stripped month set so the orphan-LS
              // price estimate stays anchored to the same paired evidence
              // even when a tile filter narrows the feed to a single
              // status — same precedent as the canvas's allGroups
              // threading (GutschriftCheckPage.tsx → MatchGraph). #1395 —
              // a suppressed doc must not anchor a price estimate.
              allGroups={suppressedStripped}
              // #1397 — a badge click toggles its document into the
              // preview panel (clicking the active badge again closes it),
              // mirroring the editor canvas's onNodeClick toggle so the two
              // views share the same "click to preview / click again to
              // dismiss" behaviour.
              selectedDocId={selectedDocId}
              onBadgeClick={(entityId) =>
                setSelectedDocId((prev) => (prev === entityId ? null : entityId))
              }
            />
          )}
        </div>
        {/* #1397 — preview panel lifted into the feed page, reused from the
            editor as-is. Mounted as a flex sibling of ``__main`` (the same
            shell slot the editor's canvas gives ``DocumentPreviewPanel``):
            the panel returns null when ``selectedDocId`` is null, so the
            feed (``flex: 1 1 auto``) reclaims full width on close — no
            page-level conditional needed, matching the editor's #835/#887
            keep-mounted contract. onClose clears the single source of
            truth, same as a re-click on the active badge. */}
        <DocumentPreviewPanel
          documentId={selectedDocId}
          onClose={() => setSelectedDocId(null)}
        />
      </div>
    </div>
  );
}
